import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { GlobalHeader, Button } from '@ver-uds/react';
import { EverifyUserRole } from '@vis-auth/vis-user-client';
import { AccountType } from 'eve-account-client';
import { Icon } from '@ver-uds/uswds-react';
import EveAuthorizationService from '../EveAuthorizationService';
import logo from '../assets/images/eve-logo.svg';
import EveRoutes from '../EveRoutes';

interface UserProps {
  firstName: string;
  lastName: string;
  roles: EverifyUserRole[];
  mfaEnabled: boolean;
}

interface AccountProps {
  accountType: AccountType;
  webServiceAccess: boolean;
}

export interface EveHeaderProps {
  user?: UserProps;
  account?: AccountProps;
  onLogOut?: () => () => void;
  showSelfTestLink: boolean;
  hideNavItems?: boolean;
  hideUserMenu?: boolean;
  userMenuLabel?: string;
  showTrainingLink?: boolean;
  hideNavigateToDashboard?: boolean;
  onNavigateToDashboard?: () => () => void;
}

const EveHeader = ({
  user,
  account,
  onLogOut,
  showSelfTestLink,
  hideNavItems,
  hideUserMenu,
  userMenuLabel,
  showTrainingLink,
  hideNavigateToDashboard,
  onNavigateToDashboard,
}: EveHeaderProps): JSX.Element => {
  const location = useLocation();
  const userFullName = user ? `${user.firstName} ${user.lastName}` : null;

  const userNavItems = !user?.mfaEnabled
    ? [
        <NavLink
          key={EveRoutes.USER_PROFILE}
          to={EveRoutes.USER_PROFILE}
          exact
          activeClassName={GlobalHeader.activeClass}
        >
          User Profile
        </NavLink>,

        <NavLink
          key={EveRoutes.USER_PASSWORD_EDIT}
          to={EveRoutes.USER_PASSWORD_EDIT}
          exact
          activeClassName={GlobalHeader.activeClass}
        >
          Change Password
        </NavLink>,
      ]
    : [
        <NavLink
          key={EveRoutes.USER_PROFILE}
          to={EveRoutes.USER_PROFILE}
          exact
          activeClassName={GlobalHeader.activeClass}
        >
          User Profile
        </NavLink>,
      ];

  const eveAuthorizationService =
    user && account
      ? new EveAuthorizationService({
          userRoles: user.roles,
          accountType: account.accountType,
          webServiceAccess: account.webServiceAccess,
        })
      : undefined;

  const NavItems = (): JSX.Element | null => {
    if (hideNavItems) {
      return null;
    }

    return (
      <GlobalHeader.NavItems>
        <NavLink to={EveRoutes.HOME} exact activeClassName={GlobalHeader.activeClass}>
          Home
        </NavLink>

        {eveAuthorizationService?.authorizeCasesPaths() && (
          <NavLink to={EveRoutes.CASES_VIEW} exact activeClassName={GlobalHeader.activeClass}>
            Cases
          </NavLink>
        )}

        {eveAuthorizationService?.authorizeClientPaths() && (
          <NavLink to={EveRoutes.COMPANY_CLIENTS_VIEW} exact activeClassName={GlobalHeader.activeClass}>
            Clients
          </NavLink>
        )}

        {eveAuthorizationService?.authorizeCorporatePaths() && (
          <GlobalHeader.Menu
            id="menu-3"
            title="Company Locations"
            current={
              location.pathname.startsWith(EveRoutes.CORPORATE_COMPANY_LOCATIONS_PREFIX) ||
              location.pathname.startsWith(EveRoutes.CORPORATE_COMPANY_LOCATIONS_USERS_PREFIX)
            }
          >
            <NavLink to={EveRoutes.CORPORATE_COMPANY_LOCATIONS_VIEW} exact activeClassName={GlobalHeader.activeClass}>
              Manage Company Locations
            </NavLink>
            <NavLink to={EveRoutes.CORPORATE_COMPANY_LOCATIONS_USERS} exact activeClassName={GlobalHeader.activeClass}>
              Manage Company Location Users
            </NavLink>
          </GlobalHeader.Menu>
        )}
        <NavLink to={EveRoutes.REPORTS} exact activeClassName={GlobalHeader.activeClass}>
          Reports
        </NavLink>

        {eveAuthorizationService?.authorizeWebServices() && showSelfTestLink && (
          <GlobalHeader.Menu
            id="menu-4"
            title="Web Services"
            current={location.pathname === EveRoutes.WS_SELF_TEST_SIMULATOR}
          >
            <NavLink to={EveRoutes.WS_SELF_TEST_SIMULATOR} exact activeClassName={GlobalHeader.activeClass}>
              Self Test Emulator
            </NavLink>
          </GlobalHeader.Menu>
        )}
        <GlobalHeader.Menu
          id="menu-5"
          title="Resources"
          current={location.pathname.startsWith(EveRoutes.RESOURCES_PREFIX) || location.pathname === EveRoutes.TUTORIAL}
        >
          <NavLink
            data-testid="resources_VER"
            to={EveRoutes.RESOURCES_VIEW}
            exact
            activeClassName={GlobalHeader.activeClass}
          >
            View Essential Resources
          </NavLink>
          <NavLink to={EveRoutes.TUTORIAL} exact activeClassName={GlobalHeader.activeClass}>
            Take Tutorial
          </NavLink>
          {showTrainingLink && (
            <NavLink to={EveRoutes.RESOURCES_VIEW_TRAINING} exact activeClassName={GlobalHeader.activeClass}>
              View E-Verify+ Training
            </NavLink>
          )}
          <NavLink to={EveRoutes.RESOURCES_MANUAL} exact activeClassName={GlobalHeader.activeClass}>
            View User Manual
          </NavLink>
          <NavLink to={EveRoutes.RESOURCES_NEWS} exact activeClassName={GlobalHeader.activeClass}>
            E-Verify News and Announcements
          </NavLink>
          <NavLink to={EveRoutes.RESOURCES_CONTACT_US} exact activeClassName={GlobalHeader.activeClass}>
            Contact Us
          </NavLink>
        </GlobalHeader.Menu>
      </GlobalHeader.NavItems>
    );
  };

  return (
    <GlobalHeader
      logo={<img src={logo} alt="E-Verify logo" height={28} />}
      navItems={<NavItems />}
      rightNavItems={
        hideUserMenu ? undefined : (
          <GlobalHeader.NavItems>
            <GlobalHeader.Menu
              id="menu-right"
              title={userMenuLabel || userFullName}
              current={([
                EveRoutes.COMPANY_PROFILE_VIEW,
                EveRoutes.CORPORATE_PROFILE_VIEW,
                EveRoutes.USER_PROFILE,
                EveRoutes.USER_PROFILE_EDIT,
                EveRoutes.USER_PASSWORD_EDIT,
              ] as string[]).includes(location.pathname)}
              icon="user"
            >
              {!hideNavItems && eveAuthorizationService?.authorizeCompanyPaths() && (
                <GlobalHeader.Menu
                  id="header-menu__company"
                  title="Company Account"
                  current={
                    ([EveRoutes.COMPANY_PROFILE_VIEW, EveRoutes.COMPANY_CLOSE] as string[]).includes(
                      location.pathname,
                    ) ||
                    (location.pathname.startsWith(EveRoutes.COMPANY_USERS_PREFIX) &&
                      !location.pathname.startsWith(EveRoutes.USER_PROFILE_PREFIX))
                  }
                >
                  <NavLink to={EveRoutes.COMPANY_PROFILE_VIEW} exact activeClassName={GlobalHeader.activeClass}>
                    Company Profile
                  </NavLink>
                  <NavLink to={EveRoutes.COMPANY_USERS} exact activeClassName={GlobalHeader.activeClass}>
                    Manage Users
                  </NavLink>
                  <NavLink to={EveRoutes.COMPANY_CLOSE} exact activeClassName={GlobalHeader.activeClass}>
                    Close Company Account
                  </NavLink>
                </GlobalHeader.Menu>
              )}
              {!hideNavItems && eveAuthorizationService?.authorizeCorporatePaths() && (
                <GlobalHeader.Menu
                  id="header-menu__corporate"
                  title="Corporate Account"
                  current={
                    ([EveRoutes.CORPORATE_PROFILE_VIEW, EveRoutes.CORPORATE_CLOSE] as string[]).includes(
                      location.pathname,
                    ) || location.pathname.includes(EveRoutes.CORPORATE_USERS_PREFIX)
                  }
                >
                  <NavLink to={EveRoutes.CORPORATE_PROFILE_VIEW} exact activeClassName={GlobalHeader.activeClass}>
                    Corporate Profile
                  </NavLink>
                  <NavLink to={EveRoutes.CORPORATE_USERS} exact activeClassName={GlobalHeader.activeClass}>
                    Manage Administrators
                  </NavLink>
                  <NavLink to={EveRoutes.CORPORATE_CLOSE} exact activeClassName={GlobalHeader.activeClass}>
                    Close Corporate Account
                  </NavLink>
                </GlobalHeader.Menu>
              )}

              {!hideNavItems &&
                (eveAuthorizationService?.authorizeCompanyPaths() ||
                eveAuthorizationService?.authorizeCorporatePaths() ? (
                  <GlobalHeader.Menu
                    id="header-menu__user"
                    title="User Account"
                    current={location.pathname.startsWith(EveRoutes.USER_PROFILE_PREFIX)}
                  >
                    {userNavItems}
                  </GlobalHeader.Menu>
                ) : (
                  userNavItems
                ))}
              {user?.mfaEnabled && !hideNavigateToDashboard && (
                <NavLink
                  key={EveRoutes.LOGIN_DOT_GOV_LOGIN}
                  to={EveRoutes.LOGIN_DOT_GOV_LOGIN}
                  exact
                  activeClassName={GlobalHeader.activeClass}
                  onClick={onNavigateToDashboard?.()}
                >
                  <Icon name="arrow_back" className="margin-right-05" /> Dashboard
                </NavLink>
              )}
              <Button variant="destructive" onClick={onLogOut?.()}>
                Log Out
              </Button>
            </GlobalHeader.Menu>
          </GlobalHeader.NavItems>
        )
      }
    />
  );
};

export default EveHeader;
